import colorPallete, { bodyFonts, elevation, headlineFonts, labelFonts } from "@DSG/MobiusOverrides/styles/variables";
import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import { WidgetDefinition } from "@insite/client-framework/Types/ContentItemDefinitions";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import Img from "@insite/mobius/Img/Img";
import { Button, GridContainer, GridItem, Link } from "@insite/mobius/index";
import Typography from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import parse from "html-react-parser"
import * as React from "react";
import styled, { css } from "styled-components";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import translate from "@insite/client-framework/Translate";
import { getPageLinkByPageType, useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";

type Props = OwnProps & HasProduct;
// this is used to help ensure that the names match between props.fields and definition.fieldDefinitions[].name
const enum fields {

    showIconImageFirst = "showIconImageFirst",
    showHeadingFirst = "showHeadingFirst",
    showSubHeadingFirst = "showSubHeadingFirst",
    showButtonFirst = "showButtonFirst",
    imageIconFirst = "imageIconFirst",
    headingTextFirst = "headingTextFirst",
    subHeadingTextFirst = "subHeadingTextFirst",
    buttonTextFirst = "buttonTextFirst",
    buttonLinkFirst = "buttonLinkFirst",
    openInNewWindowFirst = "openInNewWindowFirst",
    buttonVariantFirst = "buttonVariantFirst",

    showIconImageSecond = "showIconImageSecond",
    showHeadingSecond = "showHeadingSecond",
    showSubHeadingSecond = "showSubHeadingSecond",
    showButtonSecond = "showButtonSecond",
    imageIconSecond = "imageIconSecond",
    headingTextSecond = "headingTextSecond",
    subHeadingTextSecond = "subHeadingTextSecond",
    buttonTextSecond = "buttonTextSecond",
    buttonLinkSecond = "buttonLinkSecond",
    openInNewWindowSecond = "openInNewWindowSecond",
    buttonVariantSecond = "buttonVariantSecond",

    showIconImageThird = "showIconImageThird",
    showHeadingThird = "showHeadingThird",
    showSubHeadingThird = "showSubHeadingThird",
    showButtonThird = "showButtonThird",
    imageIconThird = "imageIconThird",
    headingTextThird = "headingTextThird",
    subHeadingTextThird = "subHeadingTextThird",
    buttonTextThird = "buttonTextThird",
    buttonLinkThird = "buttonLinkThird",
    openInNewWindowThird = "openInNewWindowThird",
    buttonVariantThird = "buttonVariantThird",
}
const enum variants {
    primary = "dsgBtn dsgPrimaryBtn",
    secondary = "dsgBtn dsgSecondaryBtn",
    primaryBlack = "dsgBtn dsgPrimaryDarkBtn",
    secondaryBlack = "dsgBtn dsgSecondaryDarkBtn",
}
interface OwnProps extends WidgetProps  {
    fields: {
        [fields.showIconImageFirst]: boolean;
        [fields.showHeadingFirst]: boolean;
        [fields.showSubHeadingFirst]: boolean;
        [fields.showButtonFirst]: boolean;
        [fields.imageIconFirst]: string;
        [fields.headingTextFirst]: string;
        [fields.subHeadingTextFirst]: string;
        [fields.buttonTextFirst]: string;
        [fields.buttonLinkFirst]: LinkFieldValue;
        [fields.openInNewWindowFirst]: boolean;
        [fields.buttonVariantFirst]: variants;
        [fields.showIconImageSecond]: boolean;
        [fields.showHeadingSecond]: boolean;
        [fields.showSubHeadingSecond]: boolean;
        [fields.showButtonSecond]: boolean;
        [fields.imageIconSecond]: string;
        [fields.headingTextSecond]: string;
        [fields.subHeadingTextSecond]: string;
        [fields.buttonTextSecond]: string;
        [fields.buttonLinkSecond]: LinkFieldValue;
        [fields.openInNewWindowSecond]: boolean;
        [fields.buttonVariantSecond]: variants;
        [fields.showIconImageThird]: boolean;
        [fields.showHeadingThird]: boolean;
        [fields.showSubHeadingThird]: boolean;
        [fields.showButtonThird]: boolean;
        [fields.imageIconThird]: string;
        [fields.headingTextThird]: string;
        [fields.subHeadingTextThird]: string;
        [fields.buttonTextThird]: string;
        [fields.buttonLinkThird]: LinkFieldValue;
        [fields.openInNewWindowThird]: boolean;
        [fields.buttonVariantThird]: variants;
    };
}

export interface DsgThreeColumnBoxWidgetStyles {
    wrapper?: InjectableCss;
    boxGridContainer?: GridContainerProps;
    boxGridItem?: GridItemProps;
    imgWrapper?: InjectableCss;
    headingText?: InjectableCss;
    subHeadingText?: InjectableCss;
    contentWrapper?: InjectableCss;
    linkText?: InjectableCss;
    linkWrapper?: InjectableCss;
}


export const dsgThreeColumnBoxWidgetStyles: DsgThreeColumnBoxWidgetStyles = {
    boxGridContainer: {
        gap: 0,
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
            breakpointMediaQueries(
                theme,
                [
                    null,
                    null,
                    css`
                        > div {
                            justify-content: space-between;
                        }
                    `,
                ],
                "min",
            )}
            ${({ theme }: { theme: BaseTheme }) =>
            breakpointMediaQueries(
                theme,
                [
                    null,
                    css`
                        > div {
                            row-gap: 24px;
                        }
                    `,
                ],
                "max",
            )}
            ${({ theme }: { theme: BaseTheme }) =>
            breakpointMediaQueries(
                theme,
                [
                    null,
                    null,
                    css`
                        > div {
                            flex-direction: row !important;
                            > div {
                                max-width: calc(100%/3 - 16px) !important;
                                flex-basis: calc(100%/3 - 16px) !important;
                            }
                        }
                    `,
                ],
                "minMax",
            )}
        `
    },
    boxGridItem: {
        width: [12, 12, 4, 4, 4],
        css: css`
            border: 1px solid ${colorPallete.grey500};
            border-radius: 8px;
            box-shadow: ${elevation.dp04};
            padding: 24px 48px;
            ${({ theme }: { theme: BaseTheme }) =>
            breakpointMediaQueries(
                theme,
                [
                    null,
                    null,
                    css`
                        max-width: calc(100%/3 - 16px);
                        flex-basis: calc(100%/3 - 16px);
                        padding: 24px 16px;
                        .dsgBtn {
                            max-width: 289px;
                            width: 100%;    
                        }
                    `,
                    css`
                        padding: 40px 48px;
                    `,
                ],
                "min",
            )}
            @media screen and (min-width: 1024px) and (max-width: 1366px) {
                padding: 24px 16px;
                .dsgBtn {
                    padding-left: 6px;
                    padding-right: 6px; 
                }
            }
        `
    },
    contentWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            text-align: center;
            height: 100%;
            width: 100%;
            color: ${colorPallete.grey800};
        `,
    },
    imgWrapper: {
        css: css`
            max-width: 56px;
            margin: 0 auto 8px;
            max-height: 56px;
            min-height: 56px;
        `
    },
    headingText: {
        css: css`
            font: ${headlineFonts.headline5SemiBoldSm.fontWeight} ${headlineFonts.headline5SemiBoldSm.fontSize}/${headlineFonts.headline5SemiBoldSm.lineHeight} ${headlineFonts.headline5SemiBoldSm.fontFamily};
            margin-bottom: 8px;
            ${({ theme }: { theme: BaseTheme }) =>
            breakpointMediaQueries(
                theme,
                [
                    null,
                    null,
                    null,
                    css`
                        font-size: ${headlineFonts.headline5SemiBoldLg.fontSize};
                        line-height: ${headlineFonts.headline5SemiBoldLg.lineHeight};
                    `,
                ],
                "min",
            )}
        `
    },
    subHeadingText: {
        css: css`
            margin-bottom: 24px;
            p {
                font: ${bodyFonts.body2RegularLg.fontWeight} ${bodyFonts.body2RegularLg.fontSize}/${bodyFonts.body2RegularLg.lineHeight} ${bodyFonts.body2RegularLg.fontFamily};
                color: ${colorPallete.grey800};
                &:last-child {
                    margin-bottom: 0;
                }
            }
        `
    },
    linkWrapper: {
        css: css`
            justify-content: center;
            margin-top: auto;
        `
    },
    linkText: {
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
            breakpointMediaQueries(
                theme,
                [
                    null,
                    css`
                        width: 100%;
                    `,
                ],
                "max",
            )}
        `
    },
    wrapper: {
        css: css`
            margin-bottom: 24px;
            ${({ theme }: { theme: BaseTheme }) =>
            breakpointMediaQueries(
                theme,
                [
                    null,
                    null,
                    css`
                        margin-bottom: 40px;
                    `,
                ],
                "min",
            )}
        `
    }
}

const styles = dsgThreeColumnBoxWidgetStyles;

const DsgThreeColumnBoxWidget: React.FC<Props> = props => {
   return (
        <>
            <StyledWrapper {...styles.wrapper}>
                <GridContainer {...styles.boxGridContainer}>
                    <GridItem {...styles.boxGridItem}>
                        <StyledWrapper {...styles.contentWrapper}>
                            {props.fields.showIconImageFirst &&
                                <StyledWrapper {...styles.imgWrapper}>
                                    <Img src={props.fields.imageIconFirst}/>
                                </StyledWrapper>
                            }
                            {props.fields.showHeadingFirst &&
                                <Typography as="h2"  {...styles.headingText}>
                                    {parse(props.fields.headingTextFirst, parserOptions)}
                                </Typography>
                            }
                            {props.fields.showSubHeadingFirst &&
                                <StyledWrapper {...styles.subHeadingText}>
                                    {parse(props.fields.subHeadingTextFirst, parserOptions)}
                                </StyledWrapper>
                            }
                            {props.fields.showButtonFirst &&
                                <StyledWrapper {...styles.linkWrapper}>
                                    <Link href={useGetLink(props.fields.buttonLinkFirst).url}
                                        {...styles.linkText}
                                        target={props.fields.openInNewWindowFirst ? "_blank" : ""}
                                        className={props.fields.buttonVariantFirst}
                                    >
                                        {props.fields.buttonTextFirst}
                                    </Link>
                                </StyledWrapper>
                            }
                        </StyledWrapper>
                    </GridItem>
                    <GridItem {...styles.boxGridItem}>
                    <StyledWrapper {...styles.contentWrapper}>
                            {props.fields.showIconImageSecond &&
                                <StyledWrapper {...styles.imgWrapper}>
                                    <Img src={props.fields.imageIconSecond}/>
                                </StyledWrapper>
                            }
                            {props.fields.showHeadingSecond &&
                                <Typography as="h2"  {...styles.headingText}>
                                    {parse(props.fields.headingTextSecond, parserOptions)}
                                </Typography>
                            }
                            {props.fields.showSubHeadingSecond &&
                                <StyledWrapper {...styles.subHeadingText}>
                                    {parse(props.fields.subHeadingTextSecond, parserOptions)}
                                </StyledWrapper>
                            }
                            {props.fields.showButtonSecond &&
                                <StyledWrapper {...styles.linkWrapper}>
                                    <Link href={useGetLink(props.fields.buttonLinkSecond).url}
                                        {...styles.linkText}
                                        target={props.fields.openInNewWindowSecond ? "_blank" : ""}
                                        className={props.fields.buttonVariantSecond}
                                    >
                                        {props.fields.buttonTextSecond}
                                    </Link>
                                </StyledWrapper>
                            }
                        </StyledWrapper>
                    </GridItem>
                    <GridItem {...styles.boxGridItem}>
                    <StyledWrapper {...styles.contentWrapper}>
                            {props.fields.showIconImageThird &&
                                <StyledWrapper {...styles.imgWrapper}>
                                    <Img src={props.fields.imageIconThird}/>
                                </StyledWrapper>
                            }
                            {props.fields.showHeadingThird &&
                                <Typography as="h2"  {...styles.headingText}>
                                    {parse(props.fields.headingTextThird, parserOptions)}
                                </Typography>
                            }
                            {props.fields.showSubHeadingThird &&
                                <StyledWrapper {...styles.subHeadingText}>
                                    {parse(props.fields.subHeadingTextThird, parserOptions)}
                                </StyledWrapper>
                            }
                            {props.fields.showButtonThird &&
                                <StyledWrapper {...styles.linkWrapper}>
                                    <Link href={useGetLink(props.fields.buttonLinkThird).url}
                                        {...styles.linkText}
                                        target={props.fields.openInNewWindowThird ? "_blank" : ""}
                                        className={props.fields.buttonVariantThird ? props.fields.buttonVariantThird : variants.primary}
                                    >
                                        {props.fields.buttonTextThird}
                                    </Link>
                                </StyledWrapper>
                            }
                        </StyledWrapper>
                    </GridItem>
                </GridContainer>
            </StyledWrapper>
        </>
    );
    
};



const definition: WidgetDefinition = {
    group: "Common",
    fieldDefinitions: [
        {
            name: fields.showIconImageFirst,
            displayName: "Column 1 Show Image",
            editorTemplate: "CheckboxField",
            defaultValue: true,
            fieldType: "Contextual",
            sortOrder: 1,
        },
        {
            fieldType: "Contextual",
            displayName: "Column 1 Image",
            name: fields.imageIconFirst,
            editorTemplate: "ImagePickerField",
            defaultValue: "",
            sortOrder: 2,
            isVisible: widget => widget.fields.showIconImageFirst,
        },
        {
            name: fields.showHeadingFirst,
            displayName: "Column 1 Show Heading",
            editorTemplate: "CheckboxField",
            defaultValue: true,
            fieldType: "Contextual",
            sortOrder: 3,
        },
        {
            fieldType: "General",
            name: fields.headingTextFirst,
            editorTemplate: "TextField",
            displayName: "Column 1 Heading",
            defaultValue: "",
            sortOrder: 4,
            isVisible: widget => widget.fields.showHeadingFirst,
        },
        {
            name: fields.showSubHeadingFirst,
            displayName: "Column 1 Show Sub Heading",
            editorTemplate: "CheckboxField",
            defaultValue: true,
            fieldType: "Contextual",
            sortOrder: 5,
        },
        {
            fieldType: "Contextual",
            name: fields.subHeadingTextFirst,
            editorTemplate: "RichTextField",
            displayName: "Column 1 Sub Heading",
            defaultValue: "",
            extendedConfig: { height: 170 },
            expandedToolbarButtons: {
                moreMisc: {
                    buttons: [
                        "insertLink",
                        "insertImage",
                        "insertHR",
                        "specialCharacters",
                        "insertVideo",
                        "insertFile",
                    ],
                    buttonsVisible: 3,
                },
                code: {
                    buttons: ["selectAll", "html", "help"],
                    buttonsVisible: 3,
                },
            },
            sortOrder: 6,
            isVisible: widget => widget.fields.showSubHeadingFirst,
        },
        {
            name: fields.showButtonFirst,
            displayName: "Column 1 Show Button",
            editorTemplate: "CheckboxField",
            defaultValue: true,
            fieldType: "Contextual",
            sortOrder: 7,
        },
        {
            fieldType: "General",
            name: fields.buttonTextFirst,
            editorTemplate: "TextField",
            displayName: "Column 1 Button Text",
            defaultValue: "",
            isVisible: widget => widget.fields.showButtonFirst,
            sortOrder: 8,
        },
        {
            fieldType: "Contextual",
            name: fields.buttonLinkFirst,
            displayName: "Column 1 Button Link",
            editorTemplate: "LinkField",
            defaultValue: { type: "Page", value: "" },
            isVisible: widget => widget.fields.showButtonFirst,
            sortOrder: 9,
        },
        {
            name: fields.buttonVariantFirst,
            displayName: "Column 1 Button Variant",
            editorTemplate: "DropDownField",
            options: [
                { displayName: "Primary", value: variants.primary },
                { displayName: "Secondary", value: variants.secondary },
                { displayName: "Primary Black", value: variants.primaryBlack },
                { displayName: "Secondary Black", value: variants.secondaryBlack },
            ],
            defaultValue: variants.primary,
            fieldType: "General",
            isVisible: widget => widget.fields.showButtonFirst,
            sortOrder: 10,
        },
        {
            name: fields.openInNewWindowFirst,
            editorTemplate: "CheckboxField",
            displayName: "Column 1 Open Link in New Window",
            defaultValue: true,
            fieldType: "General",
            isVisible: widget => widget.fields.showButtonFirst,
            sortOrder: 11,
        },


        {
            name: fields.showIconImageSecond,
            displayName: "Column 2 Show Icon",
            editorTemplate: "CheckboxField",
            defaultValue: true,
            fieldType: "Contextual",
            sortOrder: 12,
        },
        {
            fieldType: "Contextual",
            displayName: "Column 2 Image",
            name: fields.imageIconSecond,
            editorTemplate: "ImagePickerField",
            defaultValue: "",
            sortOrder: 13,
            isVisible: widget => widget.fields.showIconImageSecond,
        },
        {
            name: fields.showHeadingSecond,
            displayName: "Column 2 Show Heading",
            editorTemplate: "CheckboxField",
            defaultValue: true,
            fieldType: "Contextual",
            sortOrder: 14,
        },
        {
            fieldType: "General",
            name: fields.headingTextSecond,
            editorTemplate: "TextField",
            displayName: "Column 2 Heading",
            defaultValue: "",
            sortOrder: 15,
            isVisible: widget => widget.fields.showHeadingSecond,
        },
        {
            name: fields.showSubHeadingSecond,
            displayName: "Column 2 Show Sub Heading",
            editorTemplate: "CheckboxField",
            defaultValue: true,
            fieldType: "Contextual",
            sortOrder: 16,
        },
        {
            fieldType: "Contextual",
            name: fields.subHeadingTextSecond,
            editorTemplate: "RichTextField",
            displayName: "Column 2 Sub Heading",
            defaultValue: "",
            extendedConfig: { height: 170 },
            expandedToolbarButtons: {
                moreMisc: {
                    buttons: [
                        "insertLink",
                        "insertImage",
                        "insertHR",
                        "specialCharacters",
                        "insertVideo",
                        "insertFile",
                    ],
                    buttonsVisible: 3,
                },
                code: {
                    buttons: ["selectAll", "html", "help"],
                    buttonsVisible: 3,
                },
            },
            sortOrder: 17,
            isVisible: widget => widget.fields.showSubHeadingSecond,
        },
        {
            name: fields.showButtonSecond,
            displayName: "Column 2 Show Button",
            editorTemplate: "CheckboxField",
            defaultValue: true,
            fieldType: "Contextual",
            sortOrder: 18,
        },
        {
            fieldType: "General",
            name: fields.buttonTextSecond,
            editorTemplate: "TextField",
            displayName: "Column 2 Button Text",
            defaultValue: "",
            isVisible: widget => widget.fields.showButtonSecond,
            sortOrder: 19,
        },
        {
            fieldType: "Contextual",
            name: fields.buttonLinkSecond,
            displayName: "Column 2 Button Link",
            editorTemplate: "LinkField",
            defaultValue: { type: "Page", value: "" },
            isVisible: widget => widget.fields.showButtonSecond,
            sortOrder: 20,
        },
        {
            name: fields.buttonVariantSecond,
            displayName: "Column 2 Button Variant",
            editorTemplate: "DropDownField",
            options: [
                { displayName: "Primary", value: variants.primary },
                { displayName: "Secondary", value: variants.secondary },
                { displayName: "Primary Black", value: variants.primaryBlack },
                { displayName: "Secondary Black", value: variants.secondaryBlack },
            ],
            defaultValue: variants.primary,
            fieldType: "General",
            isVisible: widget => widget.fields.showButtonSecond,
            sortOrder: 21,
        },
        {
            name: fields.openInNewWindowSecond,
            editorTemplate: "CheckboxField",
            defaultValue: true,
            fieldType: "General",
            isVisible: widget => widget.fields.showButtonSecond,
            sortOrder: 22,
            displayName: "Column 2 Open Link in New Window",
        },


        {
            name: fields.showIconImageThird,
            displayName: "Column 3 Show Icon",
            editorTemplate: "CheckboxField",
            defaultValue: true,
            fieldType: "Contextual",
            sortOrder: 23,
        },
        {
            fieldType: "Contextual",
            displayName: "Column 3 Image",
            name: fields.imageIconThird,
            editorTemplate: "ImagePickerField",
            defaultValue: "",
            sortOrder: 24,
            isVisible: widget => widget.fields.showIconImageThird,
        },
        {
            name: fields.showHeadingThird,
            displayName: "Column 3 Show Heading",
            editorTemplate: "CheckboxField",
            defaultValue: true,
            fieldType: "Contextual",
            sortOrder: 25,
        },
        {
            fieldType: "General",
            name: fields.headingTextThird,
            editorTemplate: "TextField",
            displayName: "Column 3 Heading",
            defaultValue: "",
            sortOrder: 26,
            isVisible: widget => widget.fields.showHeadingThird,
        },
        {
            name: fields.showSubHeadingThird,
            displayName: "Column 3 Show Sub Heading",
            editorTemplate: "CheckboxField",
            defaultValue: true,
            fieldType: "Contextual",
            sortOrder: 27,
        },
        {
            fieldType: "Contextual",
            name: fields.subHeadingTextThird,
            editorTemplate: "RichTextField",
            displayName: "Column 3 Sub Heading",
            defaultValue: "",
            extendedConfig: { height: 170 },
            expandedToolbarButtons: {
                moreMisc: {
                    buttons: [
                        "insertLink",
                        "insertImage",
                        "insertHR",
                        "specialCharacters",
                        "insertVideo",
                        "insertFile",
                    ],
                    buttonsVisible: 3,
                },
                code: {
                    buttons: ["selectAll", "html", "help"],
                    buttonsVisible: 3,
                },
            },
            sortOrder: 28,
            isVisible: widget => widget.fields.showSubHeadingThird,
        },
        {
            name: fields.showButtonThird,
            displayName: "Column 3 Show Button",
            editorTemplate: "CheckboxField",
            defaultValue: true,
            fieldType: "Contextual",
            sortOrder: 29,
        },
        {
            fieldType: "General",
            name: fields.buttonTextThird,
            editorTemplate: "TextField",
            displayName: "Column 3 Button Text",
            defaultValue: "",
            isVisible: widget => widget.fields.showButtonThird,
            sortOrder: 30,
        },
        {
            fieldType: "Contextual",
            name: fields.buttonLinkThird,
            displayName: "Column 3 Button Link",
            editorTemplate: "LinkField",
            defaultValue: { type: "Page", value: "" },
            isVisible: widget => widget.fields.showButtonThird,
            sortOrder: 31,
        },
        {
            name: fields.buttonVariantThird,
            displayName: "Column 3 Button Variant",
            editorTemplate: "DropDownField",
            options: [
                { displayName: "Primary", value: variants.primary},
                { displayName: "Secondary", value: variants.secondary },
                { displayName: "Primary Black", value: variants.primaryBlack },
                { displayName: "Secondary Black", value: variants.secondaryBlack },
            ],
            defaultValue: variants.primary,
            fieldType: "General",
            isVisible: widget => widget.fields.showButtonThird,
            sortOrder: 32,
        },
        {
            name: fields.openInNewWindowThird,
            editorTemplate: "CheckboxField",
            defaultValue: true,
            fieldType: "General",
            isVisible: widget => widget.fields.showButtonThird,
            sortOrder: 33,
            displayName: "Column 3 Open Link in New Window",
        },
    ],
};

const widgetModule: WidgetModule = {
    component: withProduct(DsgThreeColumnBoxWidget),
    definition,
};

export default widgetModule;
